/**
 * Posicion fina de lectura en tira vertical.
 *
 * El servidor solo guarda `lastPageRead`, un numero de pagina. Eso vale para
 * un manga, donde una pagina es una pantalla, pero no para un manhwa: en
 * "Eternally Regressing Knight" un capitulo son 12 paginas de 800x14435, que
 * a lo ancho de un iPhone son **7.000 px cada una, unas ocho pantallas**.
 * Volver a "la pagina 5" te deja en lo alto de esa pagina, que puede estar a
 * ocho pantallas de donde ibas de verdad.
 *
 * Asi que ademas del numero de pagina se apunta cuanto llevabas dentro de
 * ella. Va en localStorage y no en el servidor porque el servidor no tiene
 * donde meterlo, y porque es dato de este aparato: el progreso que se
 * comparte sigue siendo `lastPageRead`.
 */

const PREFIJO = 'yomou.posicion.'

export interface Posicion {
  /** Indice de pagina dentro del capitulo. */
  indice: number
  /** Pixeles de esa pagina que quedan por encima del borde de la pantalla. */
  desplazamiento: number
}

function clave(capituloId: number): string {
  return PREFIJO + capituloId
}

export function guardarPosicion(capituloId: number, posicion: Posicion): void {
  try {
    localStorage.setItem(
      clave(capituloId),
      `${posicion.indice}:${Math.round(posicion.desplazamiento)}`,
    )
  } catch {
    // El almacenamiento puede estar lleno o bloqueado; no es motivo para
    // romper la lectura, solo se pierde la posicion fina.
  }
}

export function leerPosicion(capituloId: number): Posicion | null {
  let crudo: string | null = null
  try {
    crudo = localStorage.getItem(clave(capituloId))
  } catch {
    return null
  }
  if (!crudo) return null

  const [uno, dos] = crudo.split(':')
  const indice = Number(uno)
  const desplazamiento = Number(dos)
  if (!Number.isFinite(indice) || !Number.isFinite(desplazamiento)) return null
  return { indice, desplazamiento: Math.max(0, desplazamiento) }
}

export function olvidarPosicion(capituloId: number): void {
  try {
    localStorage.removeItem(clave(capituloId))
  } catch {
    // Igual que al guardar: no pasa nada por dejarla ahi
  }
}
